/**
 * Acquisition-source capture (first-touch). Derives the channel a signup came
 * from and stamps localStorage so buildAuthHeaders() ships it as X-Source on
 * the first authenticated request (-> users.source at signup via
 * getOrCreateUser; see worker/src/lib/source.ts + db.ts).
 *
 *   classifySource()       — pure: ?source=/utm_source -> that; ?ref= ->
 *                            'referral'; search-engine referrer -> 'seo';
 *                            else 'organic'. Normalized lowercase [a-z0-9_].
 *   captureSourceFromUrl() — call ONCE, early (main.tsx, before render) so the
 *                            stamp lands before the first auth handshake.
 *                            First-touch wins (never overwrites).
 *   getSource()            — read stamp (buildAuthHeaders).
 *
 * Mirrors referral-client.ts but synchronous + no click-log: users.source IS
 * the attribution, no separate funnel-top row needed.
 */
const SOURCE_KEY = 'piccard_source'
const MAX_LEN = 24

/** Lowercase + collapse non-[a-z0-9_] to '_', strip leading/trailing _, capped. */
function normalize(s: string): string {
  return (
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, MAX_LEN) || 'organic'
  )
}

const SEARCH_HOSTS =
  /(^|\.)google\.|(^|\.)bing\.com|(^|\.)yahoo\.|duckduckgo\.com|baidu\.com|(^|\.)naver\.com|yandex\.com/

/**
 * Pure channel classifier. Exported for unit tests.
 *   explicit ?source=/utm_source wins (manual spike tagging)
 *   then ?ref= -> 'referral'
 *   then a search-engine referrer -> 'seo'
 *   else 'organic' (direct / unknown / non-search referrer)
 */
export function classifySource(params: URLSearchParams, referrer: string | null): string {
  const explicit = params.get('source') ?? params.get('utm_source')
  if (explicit) return normalize(explicit)
  if (params.get('ref')) return 'referral'
  if (referrer) {
    try {
      const host = new URL(referrer).hostname
      if (SEARCH_HOSTS.test(host)) return 'seo'
    } catch {
      // malformed referrer -> fall through to organic
    }
  }
  return 'organic'
}

/** Read + stamp first-touch source. Sync — safe to call before React renders. */
export function captureSourceFromUrl(): void {
  if (typeof window === 'undefined') return
  if (localStorage.getItem(SOURCE_KEY)) return // first-touch wins
  const src = classifySource(new URLSearchParams(window.location.search), document.referrer)
  localStorage.setItem(SOURCE_KEY, src)
}

export function getSource(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(SOURCE_KEY)
}

/** Test/reset helper. */
export function clearSource(): void {
  if (typeof window !== 'undefined') localStorage.removeItem(SOURCE_KEY)
}
